import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon } from './Icons';
import { api, type FamilyStory } from '../lib/api';
import { StoryThumbnail } from './StoryThumbnail';

interface StoryCard {
  authorId: string;
  authorName: string;
  story: FamilyStory;
  count: number;
  unread: boolean;
}

/** The quick, 24-hour catch-up surface on the main family feed. */
export function FamilyStoryTray() {
  const [stories, setStories] = useState<FamilyStory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let current = true;
    const refresh = () => {
      void api.stories('active').then((result) => {
        if (current) setStories(result.stories);
      }).catch(() => {
        if (current) setStories([]);
      }).finally(() => {
        if (current) setLoading(false);
      });
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    refresh();
    const interval = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      current = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, []);

  const cards = useMemo(() => groupByAuthor(stories.filter((story) => !story.isMine)), [stories]);
  const mine = stories.find((story) => story.isMine) ?? null;
  if (!loading && cards.length === 0 && !mine) {
    return (
      <section className="family-story-tray story-tray-empty" aria-label="Family stories">
        <Link className="story-add-card" to="/stories?compose=1">
          <span><PlusIcon /></span>
          <strong>Add your story</strong>
          <small>A quick photo or video for the family</small>
        </Link>
        <Link className="story-archive-shortcut" to="/stories">Story archive <span>›</span></Link>
      </section>
    );
  }

  return (
    <section className="family-story-tray" aria-label="Family stories">
      <div className="section-label">
        <span>Today</span>
        <Link to="/stories">All stories</Link>
      </div>
      <div className="family-story-row">
        <Link className="family-story-person your-story" to="/stories?compose=1">
          <span className={`family-story-ring ${mine ? 'has-story' : ''}`}>
            {mine ? <StoryThumbnail story={mine} size={240} /> : <span className="story-self-placeholder">You</span>}
            <span className="story-add-badge"><PlusIcon /></span>
          </span>
          <strong>Your story</strong>
          <small>{mine ? 'Add another' : 'Share an update'}</small>
        </Link>
        {cards.map((card) => (
          <Link
            className="family-story-person"
            key={card.authorId}
            to={`/stories?story=${encodeURIComponent(card.story.id)}`}
          >
            <span className={`family-story-ring ${card.unread ? 'unread' : 'viewed'}`}>
              <StoryThumbnail story={card.story} size={240} />
              {card.count > 1 && <span className="story-count">{card.count}</span>}
            </span>
            <strong>{firstName(card.authorName)}</strong>
            <small>{timeAgo(card.story.createdAt)}</small>
          </Link>
        ))}
      </div>
    </section>
  );
}

function groupByAuthor(stories: FamilyStory[]): StoryCard[] {
  const grouped = new Map<string, FamilyStory[]>();
  for (const story of stories) {
    const current = grouped.get(story.authorId) ?? [];
    current.push(story);
    grouped.set(story.authorId, current);
  }
  return [...grouped.entries()].map(([authorId, authored]) => {
    const unreadStories = authored.filter((story) => story.audiences.some((audience) => !audience.viewed));
    const story = unreadStories[unreadStories.length - 1] ?? authored[0]!;
    return {
      authorId,
      authorName: story.authorName,
      story,
      count: authored.length,
      unread: unreadStories.length > 0,
    };
  }).sort((left, right) => {
    if (left.unread !== right.unread) return left.unread ? -1 : 1;
    return right.story.createdAt - left.story.createdAt;
  });
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function timeAgo(timestamp: number): string {
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h`;
}
